import React, { useState, useMemo, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
  Theme,
  Tooltip,
  Button,
  useMediaQuery,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DescriptionIcon from '@mui/icons-material/Description';
import CaseStatusIndicator from '../common/CaseStatusIndicator';
import CaseBasicInfoSummary from './CaseBasicInfoSummary';
import CaseSymptomsSummary from './CaseSymptomsSummary';
import CaseDiagnosesSummary from './CaseDiagnosesSummary';
import CaseTreatmentsSummary from './CaseTreatmentsSummary';
import CaseSummarySummary from './CaseSummarySummary';
import { SoapNotesDialog } from './SoapNotesDialog';
import { useCases } from '../../contexts/api-contexts/CasesContext';
import { useNavigation } from '../../contexts/NavigationContext';

// Panel states
export type PanelState = 'collapsed' | 'default' | 'expanded';

// Breakpoint for narrow screens where panel should overlay
export const NARROW_SCREEN_BREAKPOINT = 700;

const PANEL_WIDTHS = {
  collapsed: 60,
  default: 320,
  expanded: 450,
};

interface StyleProps {
  panelState: PanelState;
  isComponent: boolean;
  isNarrowScreen: boolean;
  externalStyle?: React.CSSProperties;
}

const useStyles = makeStyles<StyleProps>()(
  (
    theme: Theme,
    { isComponent, panelState, isNarrowScreen, externalStyle }
  ) => ({
    panelContainer: {
      position: externalStyle?.position || (isComponent ? 'absolute' : 'fixed'),
      right: externalStyle?.right || 0,
      top: externalStyle?.top || (isComponent ? 0 : 64), // Same as AppBar height
      bottom: externalStyle?.bottom || 0,
      width: PANEL_WIDTHS[panelState],
      backgroundColor: theme.palette.background.paper,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      // On narrow screens when expanded, use stronger shadow to indicate overlay
      boxShadow:
        isNarrowScreen && panelState === 'expanded'
          ? '-4px 0 20px rgba(0, 0, 0, 0.25)'
          : '-2px 0 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      // Higher z-index on narrow screens when expanded to overlay content
      zIndex: isNarrowScreen && panelState === 'expanded' ? 1300 : 1200,
      overflowX: 'hidden',
    },
    panelHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1, 1, 1, 2),
      color: theme.palette.primary.main,
      backgroundColor: 'rgba(210, 200, 195, 0.12)',
    },
    expandCollapseButton: {
      marginLeft: 'auto',
    },
    content: {
      padding: theme.spacing(2),
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    bottomNav: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(1),
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
      minHeight: 48,
    },
    navButton: {
      minWidth: 'auto',
      textTransform: 'none',
    },
  })
);

export interface CasesPanelProps {
  panelState?: PanelState;
  onPanelStateChange?: (state: PanelState) => void;
  isNarrowScreen?: boolean;
  /** When true, panel uses absolute positioning to be contained within parent */
  isStandalone?: boolean;
  style?: React.CSSProperties;
}

export const CasesPanel: React.FC<CasesPanelProps> = ({
  panelState: externalPanelState,
  onPanelStateChange,
  isNarrowScreen = false,
  isStandalone = false,
  style: externalStyle,
}) => {
  const [internalPanelState, setInternalPanelState] =
    useState<PanelState>('collapsed');
  const [soapNotesDialogOpen, setSoapNotesDialogOpen] = useState(false);

  // On narrow screens, force collapsed state if trying to be in default
  const rawPanelState = externalPanelState || internalPanelState;
  const panelState =
    isNarrowScreen && rawPanelState === 'default' ? 'collapsed' : rawPanelState;

  // Use isStandalone prop, or fall back to legacy window check for backward compatibility
  const isComponent = isStandalone || !!(window as any).__componentsCallback;
  const { classes } = useStyles({
    panelState,
    isComponent,
    isNarrowScreen,
    externalStyle,
  });

  const navigation = useNavigation();
  const { navigationState } = navigation;
  const currentTab = navigationState.activeTab;

  const { currentCase } = useCases();

  // Use media query to determine if we have enough space for titles
  const hasSpaceForTitles = useMediaQuery('(min-height: 500px)');

  // Tab order and labels for navigation
  const tabOrder = useMemo(
    () => ['basic', 'symptoms', 'diagnoses', 'treatments', 'summary'] as const,
    []
  );
  const tabLabels = useMemo<Record<string, string>>(
    () => ({
      basic: 'Basic Info',
      symptoms: 'Symptoms',
      diagnoses: 'Diagnoses',
      treatments: 'Treatments',
      summary: 'Summary',
    }),
    []
  );

  // Check if tab is accessible based on requirements
  const canAccessTab = useCallback(
    (tab: string): boolean => {
      switch (tab) {
        case 'basic':
          // Basic information panel should always be available
          return true;
        case 'symptoms':
          // Symptoms panel should only be available if the case has a patient defined
          return !!currentCase?.patient;
        case 'diagnoses':
          // Diagnoses panel should only be available if the case has selected symptoms
          return (
            !!currentCase?.selected_symptoms &&
            currentCase.selected_symptoms.length > 0
          );
        case 'treatments':
          // Treatments panel should only be available if the case has selected diagnoses
          return (
            !!currentCase?.selected_diagnoses &&
            currentCase.selected_diagnoses.length > 0
          );
        case 'summary':
          // Summary panel should only be available if the case has selected treatments
          return (
            !!currentCase?.selected_treatments &&
            currentCase.selected_treatments.length > 0
          );
        default:
          return true;
      }
    },
    [
      currentCase?.patient,
      currentCase?.selected_symptoms,
      currentCase?.selected_diagnoses,
      currentCase?.selected_treatments,
    ]
  );

  // Get previous and next tabs based on current tab
  const navInfo = useMemo(() => {
    const currentIndex = tabOrder.indexOf(currentTab as any);
    const prevTab = currentIndex > 0 ? tabOrder[currentIndex - 1] : null;
    const nextTab =
      currentIndex < tabOrder.length - 1 ? tabOrder[currentIndex + 1] : null;

    // Helper to check tab access within memo
    const checkAccess = (tab: string | null): boolean => {
      if (!tab) return false;
      switch (tab) {
        case 'basic':
          return true;
        case 'symptoms':
          return !!currentCase?.patient;
        case 'diagnoses':
          return (
            !!currentCase?.selected_symptoms &&
            currentCase.selected_symptoms.length > 0
          );
        case 'treatments':
          return (
            !!currentCase?.selected_diagnoses &&
            currentCase.selected_diagnoses.length > 0
          );
        case 'summary':
          return (
            !!currentCase?.selected_treatments &&
            currentCase.selected_treatments.length > 0
          );
        default:
          return true;
      }
    };

    return {
      prevTab,
      nextTab,
      prevLabel: prevTab ? tabLabels[prevTab] : null,
      nextLabel: nextTab ? tabLabels[nextTab] : null,
      canGoPrev: checkAccess(prevTab),
      canGoNext: checkAccess(nextTab),
    };
  }, [currentTab, currentCase, tabOrder, tabLabels]);

  // Handle tab selection - auto-collapse on narrow screens after navigation
  const handleTabClick = useCallback(
    (tab: string) => {
      // Only navigate if the tab is accessible
      if (canAccessTab(tab)) {
        navigation.navigateToTab(tab as any);

        // Auto-collapse on narrow screens after navigation
        if (isNarrowScreen && panelState === 'expanded') {
          if (onPanelStateChange) {
            onPanelStateChange('collapsed');
          } else {
            setInternalPanelState('collapsed');
          }
        }
      }
    },
    [canAccessTab, navigation, isNarrowScreen, panelState, onPanelStateChange]
  );

  // Toggle panel states - on narrow screens, skip 'default' state
  const togglePanelState = useCallback(() => {
    let newState: PanelState;

    if (isNarrowScreen) {
      // On narrow screens: collapsed <-> expanded only
      newState = panelState === 'collapsed' ? 'expanded' : 'collapsed';
    } else {
      // On wider screens: collapsed -> default -> expanded -> collapsed
      newState =
        panelState === 'collapsed'
          ? 'default'
          : panelState === 'default'
          ? 'expanded'
          : 'collapsed';
    }

    if (onPanelStateChange) {
      onPanelStateChange(newState);
    } else {
      setInternalPanelState(newState);
    }
  }, [isNarrowScreen, panelState, onPanelStateChange]);

  // Render collapsed panel
  if (panelState === 'collapsed') {
    return (
      <Paper className={classes.panelContainer} elevation={3}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          p={1}
          onClick={togglePanelState}
          sx={{ cursor: 'pointer' }}
        >
          <ChevronLeftIcon />
        </Box>
        <Divider />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="center"
          pt={2}
        >
          {/* Show only icons in collapsed mode */}
          <Box
            onClick={() => handleTabClick('basic')}
            sx={{ cursor: 'pointer', width: '100%' }}
          >
            <CaseBasicInfoSummary
              isCollapsed={true}
              isActive={currentTab === 'basic'}
            />
          </Box>

          <Box
            onClick={() =>
              canAccessTab('symptoms') && handleTabClick('symptoms')
            }
            sx={{
              cursor: canAccessTab('symptoms') ? 'pointer' : 'not-allowed',
              width: '100%',
              opacity: canAccessTab('symptoms') ? 1 : 0.5,
            }}
          >
            <CaseSymptomsSummary
              isCollapsed={true}
              isActive={currentTab === 'symptoms'}
            />
          </Box>

          <Box
            onClick={() =>
              canAccessTab('diagnoses') && handleTabClick('diagnoses')
            }
            sx={{
              cursor: canAccessTab('diagnoses') ? 'pointer' : 'not-allowed',
              width: '100%',
              opacity: canAccessTab('diagnoses') ? 1 : 0.5,
            }}
          >
            <CaseDiagnosesSummary
              isCollapsed={true}
              isActive={currentTab === 'diagnoses'}
            />
          </Box>

          <Box
            onClick={() =>
              canAccessTab('treatments') && handleTabClick('treatments')
            }
            sx={{
              cursor: canAccessTab('treatments') ? 'pointer' : 'not-allowed',
              width: '100%',
              opacity: canAccessTab('treatments') ? 1 : 0.5,
            }}
          >
            <CaseTreatmentsSummary
              isCollapsed={true}
              isActive={currentTab === 'treatments'}
            />
          </Box>

          <Box
            onClick={() => canAccessTab('summary') && handleTabClick('summary')}
            sx={{
              cursor: canAccessTab('summary') ? 'pointer' : 'not-allowed',
              width: '100%',
              opacity: canAccessTab('summary') ? 1 : 0.5,
            }}
          >
            <CaseSummarySummary
              isCollapsed={true}
              isActive={currentTab === 'summary'}
            />
          </Box>

          <Tooltip title="Case Status" placement="left">
            <Box sx={{ mt: 2 }}>
              <CaseStatusIndicator showLabel={false} />
            </Box>
          </Tooltip>
        </Box>
      </Paper>
    );
  }

  // Render expanded or default panel
  return (
    <Paper className={classes.panelContainer} elevation={3}>
      <Box className={classes.panelHeader}>
        <Typography variant="h6">Case Information</Typography>
        <IconButton
          className={classes.expandCollapseButton}
          onClick={togglePanelState}
          size="small"
          color="primary"
        >
          {panelState === 'default' ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      <Divider />

      <Box sx={{ padding: 1 }}>
        <CaseStatusIndicator showLabel={true} />
      </Box>

      <Box sx={{ overflow: 'auto', flex: 1 }}>
        {/* Clickable summary components */}
        <Box onClick={() => handleTabClick('basic')} sx={{ cursor: 'pointer' }}>
          <CaseBasicInfoSummary isActive={currentTab === 'basic'} />
        </Box>
        <Divider />

        <Box
          onClick={() => canAccessTab('symptoms') && handleTabClick('symptoms')}
          sx={{
            cursor: canAccessTab('symptoms') ? 'pointer' : 'not-allowed',
            opacity: canAccessTab('symptoms') ? 1 : 0.5,
          }}
        >
          <CaseSymptomsSummary isActive={currentTab === 'symptoms'} />
        </Box>
        <Divider />

        <Box
          onClick={() =>
            canAccessTab('diagnoses') && handleTabClick('diagnoses')
          }
          sx={{
            cursor: canAccessTab('diagnoses') ? 'pointer' : 'not-allowed',
            opacity: canAccessTab('diagnoses') ? 1 : 0.5,
          }}
        >
          <CaseDiagnosesSummary isActive={currentTab === 'diagnoses'} />
        </Box>
        <Divider />

        <Box
          onClick={() =>
            canAccessTab('treatments') && handleTabClick('treatments')
          }
          sx={{
            cursor: canAccessTab('treatments') ? 'pointer' : 'not-allowed',
            opacity: canAccessTab('treatments') ? 1 : 0.5,
          }}
        >
          <CaseTreatmentsSummary isActive={currentTab === 'treatments'} />
        </Box>
        <Divider />

        <Box
          onClick={() => canAccessTab('summary') && handleTabClick('summary')}
          sx={{
            cursor: canAccessTab('summary') ? 'pointer' : 'not-allowed',
            opacity: canAccessTab('summary') ? 1 : 0.5,
          }}
        >
          <CaseSummarySummary isActive={currentTab === 'summary'} />
        </Box>
      </Box>

      {/* SOAP Notes Button - above bottom nav */}
      {currentCase?.id && (
        <Box sx={{ padding: 1, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<DescriptionIcon />}
            onClick={() => setSoapNotesDialogOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            Generate SOAP Notes
          </Button>
        </Box>
      )}

      {/* Bottom navigation bar */}
      <Box className={classes.bottomNav}>
        <Button
          className={classes.navButton}
          onClick={() => navInfo.prevTab && handleTabClick(navInfo.prevTab)}
          disabled={!navInfo.canGoPrev}
          size="small"
          startIcon={<NavigateBeforeIcon />}
          sx={{ visibility: navInfo.prevTab ? 'visible' : 'hidden' }}
        >
          {hasSpaceForTitles && navInfo.prevLabel}
        </Button>
        <Button
          className={classes.navButton}
          onClick={() => navInfo.nextTab && handleTabClick(navInfo.nextTab)}
          disabled={!navInfo.canGoNext}
          size="small"
          endIcon={<NavigateNextIcon />}
          sx={{ visibility: navInfo.nextTab ? 'visible' : 'hidden' }}
        >
          {hasSpaceForTitles && navInfo.nextLabel}
        </Button>
      </Box>

      {/* SOAP Notes Dialog */}
      <SoapNotesDialog
        open={soapNotesDialogOpen}
        onClose={() => setSoapNotesDialogOpen(false)}
        caseId={currentCase?.id}
      />
    </Paper>
  );
};
